/**
 * Hook pour gérer les quotas d'analytics conversationnels
 *
 * Limites freemium :
 * - Utilisateurs anonymes : 5 queries par jour
 * - Utilisateurs authentifiés : 50 queries par jour
 *
 * Les insights automatiques ne comptent pas dans le quota
 * (1 seul appel par poll, mis en cache)
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@core/contexts/AuthContext";
import { logger } from "@core/lib/logger";
import { determineUserPlan } from "@core/constants/quotas";

// Limites analytics par plan (nouveau modèle)
const ANALYTICS_LIMITS = {
  ANONYMOUS: 0, // Pas d'analytics pour les anonymes
  FREE: 10, // 10 requêtes par jour pour les gratuits
  SOLO: 50, // 50 requêtes par jour pour les Solo
} as const;

interface AnalyticsQuota {
  used: number;
  limit: number;
  remaining: number;
  resetAt: Date;
  canQuery: boolean;
}

const STORAGE_KEY = "doodates-analytics-quota";

interface QuotaData {
  count: number;
  date: string; // YYYY-MM-DD
}

export function useAnalyticsQuota() {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // Déterminer le plan de l'utilisateur (nouveau modèle 3 niveaux)
  // Pour l'instant, on considère que tous les utilisateurs authentifiés sont FREE
  // TODO: Récupérer le statut Solo depuis l'abonnement utilisateur
  const userPlan = determineUserPlan(isAuthenticated, false); // hasSoloSubscription = false pour l'instant
  const limit = ANALYTICS_LIMITS[userPlan];

  const [quota, setQuota] = useState<AnalyticsQuota>({
    used: 0,
    limit,
    remaining: limit,
    resetAt: getNextMidnight(),
    canQuery: true,
  });

  // Définir resetQuota avant son utilisation dans useEffect
  const resetQuota = useCallback(() => {
    const currentLimit = ANALYTICS_LIMITS[userPlan];
    const data: QuotaData = {
      count: 0,
      date: getTodayString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    setQuota({
      used: 0,
      limit: currentLimit,
      remaining: currentLimit,
      resetAt: getNextMidnight(),
      canQuery: true,
    });

    logger.info("Analytics quota reset", "analytics", { limit: currentLimit });
  }, [userPlan]);

  const loadQuota = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        resetQuota();
        return;
      }

      const data: QuotaData = JSON.parse(stored);
      const today = getTodayString();

      // Si la date est différente, reset le quota
      if (data.date !== today) {
        resetQuota();
        return;
      }

      const currentLimit = ANALYTICS_LIMITS[userPlan];
      const used = data.count;
      const remaining = Math.max(0, currentLimit - used);

      setQuota({
        used,
        limit: currentLimit,
        remaining,
        resetAt: getNextMidnight(),
        canQuery: remaining > 0,
      });

      logger.debug("Analytics quota loaded", "analytics", {
        used,
        limit: currentLimit,
        remaining,
      });
    } catch (error) {
      logger.error("Failed to load analytics quota", "analytics", { error });
      resetQuota();
    }
  }, [user, resetQuota]);

  // Mettre à jour la limite lorsque l'utilisateur change
  useEffect(() => {
    setQuota((prev) => ({
      ...prev,
      limit,
      remaining: limit - prev.used,
      canQuery: limit - prev.used > 0,
    }));

    // Charger le quota avec la nouvelle valeur de user
    loadQuota();
  }, [user, limit, loadQuota]);

  const incrementQuota = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const data: QuotaData = stored
        ? JSON.parse(stored)
        : { count: 0, date: getTodayString() };

      // Vérifier si on doit reset (changement de jour)
      const today = getTodayString();
      if (data.date !== today) {
        data.count = 0;
        data.date = today;
      }

      data.count += 1;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

      const currentLimit = ANALYTICS_LIMITS[userPlan];
      const used = data.count;
      const remaining = Math.max(0, currentLimit - used);

      setQuota((prev) => ({
        ...prev,
        used,
        limit: currentLimit,
        remaining,
        resetAt: getNextMidnight(),
        canQuery: remaining > 0,
      }));

      logger.info("Analytics quota incremented", "analytics", {
        used,
        limit: currentLimit,
        remaining,
      });

      return remaining > 0;
    } catch (error) {
      logger.error("Failed to increment analytics quota", "analytics", {
        error,
      });
      return false;
    }
  }, [user]);

  const checkQuota = useCallback((): boolean => {
    return quota.canQuery;
  }, [quota.canQuery]);

  const getQuotaMessage = useCallback((): string => {
    if (quota.canQuery) {
      return `${quota.remaining} requête${quota.remaining > 1 ? "s" : ""} restante${quota.remaining > 1 ? "s" : ""} aujourd'hui`;
    } else {
      const hours = Math.ceil(
        (quota.resetAt.getTime() - Date.now()) / (1000 * 60 * 60),
      );
      return `Quota épuisé. Réinitialisation dans ${hours}h`;
    }
  }, [quota]);

  return {
    quota,
    incrementQuota,
    checkQuota,
    resetQuota,
    getQuotaMessage,
  };
}

// Helpers
function getTodayString(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function getNextMidnight(): Date {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
}
